import React, { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Icon,
  IconButton,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { DDL } from 'types/general.types';
import {
  InviteCollaboratorValidations,
  SearchProfilesValidationProperties,
} from 'types/profile';
import { ReactComponent as ChevronIcon } from 'resources/images/chevron-left.svg';
import { AddCollaboratorsFormData } from './types';
import { getValidationSchema } from './utils';
import { SelectCollaborators } from './SelectCollaborators';
import { InviteCollaboratorsSummary } from './InviteCollaboratorsSummary';

export interface AddCollaboratorDialogComponentProps {
  open: boolean;
  onClose: () => void;
  profilesSearchUrl: string;
  countriesDDL: DDL;
  onInviteCollaborators: (data: AddCollaboratorsFormData) => Promise<void>;
  searchProfilesValidation?: SearchProfilesValidationProperties;
  validations?: InviteCollaboratorValidations;
}

export const AddCollaboratorDialogComponent: React.FC<AddCollaboratorDialogComponentProps> = ({
  open,
  onClose: onCloseProp,
  profilesSearchUrl,
  countriesDDL,
  onInviteCollaborators,
  validations,
}) => {
  const [step, setStep] = useState(0);

  const methods = useForm<AddCollaboratorsFormData>({
    mode: 'onChange',
    defaultValues: {
      collaborators: [],
      emailMessage: '',
    },
    resolver: yupResolver(getValidationSchema(validations)),
  });

  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
    reset,
    clearErrors,
    watch,
  } = methods;

  const collaborators = watch('collaborators');

  const resetForm = () => {
    reset();
    clearErrors();
  };

  const onClose = () => {
    setStep(0);
    resetForm();
    onCloseProp();
  };

  const onSubmit: SubmitHandler<AddCollaboratorsFormData> = async (data) => {
    try {
      await onInviteCollaborators(data);
      onClose();
    } catch (e) {
      // Proceed regardless
    }
  };

  const preventEnterSubmit: React.KeyboardEventHandler<HTMLFormElement> = (e) =>
    e.key === 'Enter' && e.preventDefault();

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalOverlay />
      <ModalContent data-testid="dialog">
        <ModalHeader data-testid="dialog-title" title="Invite Collaborator">
          {step === 1 && (
            <IconButton
              variant="text"
              onClick={() => setStep(0)}
              size="xs"
              aria-label="Go back"
              mr={1}
            >
              <Icon as={ChevronIcon} color="iconBase" width={6} height={6} />
            </IconButton>
          )}
          Invite Collaborator
        </ModalHeader>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventEnterSubmit}>
          <FormProvider {...methods}>
            {step === 0 ? (
              <SelectCollaborators
                profilesSearchUrl={profilesSearchUrl}
                countriesDDL={countriesDDL}
                collaborators={collaborators}
              />
            ) : (
              <InviteCollaboratorsSummary
                onClose={onClose}
                collaborators={collaborators}
              />
            )}

            <ModalFooter>
              <Button variant="secondary" marginRight={4} onClick={onClose}>
                Cancel
              </Button>
              {step === 0 ? (
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    setStep(1);
                  }}
                  data-testid="AddCollaboratorDialog-invite-next"
                  isDisabled={!collaborators || collaborators.length === 0}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  isDisabled={!isValid || isSubmitting}
                  data-testid="AddCollaboratorDialog-invite-submit"
                >
                  Invite
                </Button>
              )}
            </ModalFooter>
          </FormProvider>
        </form>
      </ModalContent>
    </Modal>
  );
};
